/**
 * Supported identity provider types.
 * Adding a new provider = add a value here + implement IAuthProvider.
 */
export type AuthProviderType = 'azure' | 'auth0' | 'okta' | 'mock';

/**
 * The 5 roles defined in the backend permission registry.
 * These match the CHECK constraint in admin.admin_users and the
 * role column on admin.user_institution_assignments.
 */
export type UserRole =
    | 'platform-admin'
    | 'platform-support'
    | 'org-admin'
    | 'org-manager'
    | 'org-viewer';

/**
 * Convenience constants for role strings — avoids magic literals.
 */
export const ROLES = {
    PLATFORM_ADMIN: 'platform-admin',
    PLATFORM_SUPPORT: 'platform-support',
    ORG_ADMIN: 'org-admin',
    ORG_MANAGER: 'org-manager',
    ORG_VIEWER: 'org-viewer',
} as const;

/**
 * Returns true when the role is platform-scoped (OneGroup internal).
 */
export function isPlatformRole(role: UserRole): boolean {
    return role === 'platform-admin' || role === 'platform-support';
}

/**
 * Canonical user representation — provider-agnostic.
 * Providers map their own claim shapes into this structure.
 *
 * After login, AuthContext enriches this with the backend profile
 * returned by GET /api/v1/admin-users/me.
 */
export interface User {
    id: string;
    email: string;
    name: string;
    tenantId?: string;
    role: UserRole;
    /** product:action permission strings from backend */
    permissions?: string[];
    bankId?: string;
    avatar?: string;
}

/**
 * Result of a successful authentication flow.
 */
export interface AuthResult {
    user: User;
    /** Bearer token to send to the gateway */
    accessToken: string;
    /** Raw ID token (may equal accessToken for some providers) */
    idToken: string;
    /** Unix-ms when the access token expires (0 = unknown) */
    expiresAt: number;
}

export interface LogoutOptions {
    /** Clear only local portal and provider state without ending the IdP session. */
    localOnly?: boolean;
}

/**
 * Context value exposed via useAuth().
 * Kept separate from IAuthProvider so UI concerns stay out of the provider.
 */
export interface AuthContextValue {
    user: User | null;
    loading: boolean;
    error: Error | null;
    isAuthenticated: boolean;
    login: () => Promise<void>;
    logout: (options?: LogoutOptions) => Promise<void>;
    getAccessToken: () => Promise<string | null>;
}

/**
 * Provider-agnostic auth interface.
 *
 * Every identity provider (Azure, Auth0, Okta, …) implements this
 * contract.  AuthContext depends on this interface only — never on a
 * concrete provider class.
 */
export interface IAuthProvider {
    /** Interactive login (popup or redirect). */
    login(): Promise<AuthResult>;
    /** Log the user out and clear provider-side state. */
    logout(options?: LogoutOptions): Promise<void>;
    /** Return the current user from cache/session (no network). */
    getUser(): Promise<User | null>;
    /**
     * Return a valid access token, refreshing silently if needed.
     * This MUST return an access token suitable for API calls, not an ID token.
     */
    getAccessToken(): Promise<string>;
    /** Force-refresh the token. */
    refreshToken(): Promise<string>;
    /** Handle redirect/callback after external IdP redirects back. */
    handleCallback(): Promise<AuthResult | null>;
    /** Attempt silent login (e.g. from cached session). */
    loginSilent(): Promise<AuthResult>;
    /** Check if there is a cached authenticated session. */
    isAuthenticated(): Promise<boolean>;
}
